<template>
  <el-dialog
    :model-value="modelValue"
    width="min(1200px, 96vw)"
    append-to-body
    destroy-on-close
    align-center
    class="project-overview-dialog"
    @update:model-value="emit('update:modelValue', $event)"
    @opened="onOpened"
  >
    <template #header>
      <div class="ov-header">
        <h3 class="ov-title">问题统计</h3>
        <button type="button" class="ov-select-trigger" @click="selectDialogOpen = true">
          <span>选择项目</span>
          <el-icon class="ov-select-chevron"><ArrowDown /></el-icon>
        </button>
      </div>
    </template>

    <div v-loading="loading" class="ov-body">
      <div class="ov-top">
        <!-- 问题分布 -->
        <section class="ov-card ov-dist">
          <h4 class="ov-card-title">问题分布</h4>
          <div ref="chartElRef" class="ov-chart" aria-label="问题分布半环图" />
          <div class="ov-metrics">
            <div v-for="item in distributionItems" :key="item.key" class="ov-metric">
              <span class="ov-metric-label">{{ item.label }}</span>
              <span class="ov-metric-value" :style="{ color: item.color }">{{ item.count }}</span>
            </div>
          </div>
        </section>

        <!-- 问题统计表 -->
        <section class="ov-card ov-stats">
          <div class="ov-card-head">
            <h4 class="ov-card-title">问题统计</h4>
            <div class="ov-filters">
              <div class="ov-filter">
                <span class="ov-filter-label">程度</span>
                <el-select v-model="filterSeverity" clearable placeholder="全部" class="ov-filter-select">
                  <el-option v-for="o in severityOptions" :key="o.value" :label="o.label" :value="o.value" />
                </el-select>
              </div>
              <div class="ov-filter">
                <span class="ov-filter-label">状态</span>
                <el-select v-model="filterStatus" clearable placeholder="全部" class="ov-filter-select">
                  <el-option v-for="s in statusOptions" :key="s" :label="s" :value="s" />
                </el-select>
              </div>
              <div class="ov-filter">
                <span class="ov-filter-label">创建人</span>
                <el-input v-model="filterCreator" clearable placeholder="搜索" class="ov-filter-search">
                  <template #prefix><el-icon><Search /></el-icon></template>
                </el-input>
              </div>
              <div class="ov-filter">
                <span class="ov-filter-label">处理人</span>
                <el-select
                  v-model="filterHandler"
                  clearable
                  filterable
                  placeholder="全部"
                  class="ov-filter-select"
                >
                  <el-option
                    v-for="opt in handlerFilterOptions"
                    :key="opt.value"
                    :label="opt.label"
                    :value="opt.value"
                  />
                </el-select>
              </div>
              <div class="ov-filter">
                <span class="ov-filter-label">解决时间</span>
                <el-date-picker
                  v-model="filterResolvedDate"
                  type="date"
                  value-format="YYYY-MM-DD"
                  placeholder="选择日期"
                  clearable
                  class="ov-filter-date"
                />
              </div>
            </div>
          </div>
          <el-table
            :data="filteredStatRows"
            class="ov-table"
            height="220"
            highlight-current-row
            empty-text="暂无问题数据"
          >
            <el-table-column prop="projectName" label="项目" min-width="100" show-overflow-tooltip />
            <el-table-column prop="issueLabel" label="问题" min-width="100" show-overflow-tooltip />
            <el-table-column prop="severity" label="程度" width="80" align="center">
              <template #default="{ row }">
                <span class="pill" :class="severityPillClass(row.severity)">{{ row.severity }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="status" label="状态" width="88" align="center">
              <template #default="{ row }">
                <span class="pill" :class="statusPillClass(row.status)">{{ row.status }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="creator" label="创建人" min-width="72" show-overflow-tooltip />
            <el-table-column prop="handler" label="处理人" min-width="72" show-overflow-tooltip />
            <el-table-column prop="resolvedAt" label="解决时间" width="110" align="right">
              <template #default="{ row }">{{ row.resolvedAt || '—' }}</template>
            </el-table-column>
          </el-table>
        </section>
      </div>

      <!-- 待我处理 -->
      <section class="ov-card ov-mine">
        <div class="ov-card-head">
          <div class="ov-mine-title-wrap">
            <h4 class="ov-card-title">待我处理</h4>
            <span class="ov-mine-hint">按问题严重程度、解决时间排序</span>
          </div>
          <div class="ov-filters">
            <div class="ov-filter">
              <span class="ov-filter-label">程度</span>
              <el-select v-model="mineSeverity" clearable placeholder="全部" class="ov-filter-select">
                <el-option v-for="o in severityOptions" :key="'m-' + o.value" :label="o.label" :value="o.value" />
              </el-select>
            </div>
            <div class="ov-filter">
              <span class="ov-filter-label">解决时间</span>
              <el-date-picker
                v-model="mineResolvedDate"
                type="date"
                value-format="YYYY-MM-DD"
                placeholder="选择日期"
                clearable
                class="ov-filter-date"
              />
            </div>
          </div>
        </div>
        <el-table
          :data="filteredMineRows"
          class="ov-table ov-table--stripe"
          height="260"
          highlight-current-row
          empty-text="暂无待处理问题"
        >
          <el-table-column label="TOP" width="56" align="center">
            <template #default="{ $index }">{{ $index + 1 }}</template>
          </el-table-column>
          <el-table-column prop="issueLabel" label="问题" min-width="100" show-overflow-tooltip />
          <el-table-column prop="phase" label="阶段" min-width="100" show-overflow-tooltip />
          <el-table-column prop="description" label="问题描述" min-width="160" show-overflow-tooltip />
          <el-table-column prop="category" label="类别" width="88" align="center">
            <template #default="{ row }">
              <span class="cat-badge" :style="{ background: categoryBadgeColor(row.category) }">
                {{ row.category }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="severity" label="程度" width="80" align="center">
            <template #default="{ row }">
              <span class="pill" :class="severityPillClass(row.severity)">{{ row.severity }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="88" align="center">
            <template #default="{ row }">
              <span class="pill" :class="statusPillClass(row.status)">{{ row.status }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="handler" label="处理人" min-width="72" show-overflow-tooltip />
          <el-table-column prop="resolvedAt" label="解决时间" width="110" align="right">
            <template #default="{ row }">{{ row.resolvedAt || '—' }}</template>
          </el-table-column>
          <el-table-column label="操作" width="72" align="center" fixed="right">
            <template #default="{ row }">
              <el-button link type="primary" @click="onViewIssue(row)">查看</el-button>
            </template>
          </el-table-column>
        </el-table>
      </section>
    </div>

    <ProjectOverviewSelectDialog
      v-model="selectDialogOpen"
      :projects="selectProjectRows"
      :selected-ids="selectedProjectIds"
      @confirm="onSelectConfirm"
    />
  </el-dialog>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { ArrowDown, Search } from '@element-plus/icons-vue';
import { ElMessage } from 'element-plus';
import * as echarts from 'echarts';
import type { ECharts } from 'echarts';
import type { Project } from '@/api/projectApi';
import { listProjectMemberCandidates, type ProjectMemberOption } from '@/api/projectApi';
import { listProjectIssueChecklist, type TaskItem } from '@/api/taskApi';
import { fetchAllIssueChecklistPages } from '@/utils/projectIssueStats';
import { useAuthStore } from '@/stores/useAuthStore';
import { getIssueOverviewFilters, setIssueOverviewFilters } from '@/utils/userUiPreferences';
import ProjectOverviewSelectDialog, {
  type OverviewSelectProjectRow,
} from '@/components/ProjectOverviewSelectDialog.vue';
import {
  OVERVIEW_STATUS_META,
  categoryBadgeColor,
  formatDateOnly,
  mapPool,
  mapTaskStatusToOverviewUi,
  normalizePriority,
  parseAssigneeIds,
  resolveIssueCategory,
  resolveIssueDescription,
  resolveIssueLabel,
  severityFromPriority,
  severityRank,
  type OverviewUiSeverity,
  type OverviewUiStatus,
} from '@/utils/projectIssueOverview';

interface OverviewIssueRow {
  id: number;
  projectId: number;
  projectName: string;
  issueLabel: string;
  description: string;
  category: string;
  phase: string;
  severity: OverviewUiSeverity;
  status: OverviewUiStatus;
  creator: string;
  handler: string;
  resolvedAt: string;
  dueDate: string;
  priority: string;
  assigneeUserIds: number[];
}

const PHASE_CODE_LABEL: Record<string, string> = {
  CONCEPT: '立项',
  PLAN: '产品设计和开发',
  DEVELOP: '过程设计与研发',
  VERIFY: '产品过程与确认',
  RELEASE: '量产与持续改进',
};

const props = defineProps<{
  modelValue: boolean;
  /** 个人参与的项目（默认全部纳入统计） */
  projects: Project[];
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void;
}>();

const router = useRouter();
const authStore = useAuthStore();

const loading = ref(false);
const selectDialogOpen = ref(false);
const selectedProjectIds = ref<number[]>([]);
const issueRows = ref<OverviewIssueRow[]>([]);
const issueCountByProject = ref<Map<number, number>>(new Map());
const memberNameById = ref<Map<number, string>>(new Map());

const filterSeverity = ref('');
const filterStatus = ref('');
const filterCreator = ref('');
/** 处理人筛选：选中的项目成员 userId；空字符串表示不限 */
const filterHandler = ref<number | ''>('');
const filterResolvedDate = ref<string | null>(null);
const mineSeverity = ref('');
const mineResolvedDate = ref<string | null>(null);
/** 已选项目的项目成员（处理人下拉） */
const projectMemberOptions = ref<ProjectMemberOption[]>([]);

const chartElRef = ref<HTMLElement | null>(null);
let chart: ECharts | null = null;

const severityOptions = [
  { label: '致命', value: 'P0' },
  { label: '严重', value: 'P1' },
  { label: '中风险', value: 'P2' },
  { label: '低风险', value: 'P3' },
];

const statusOptions: OverviewUiStatus[] = ['待接受', '解决中', '已解决', '已延期'];

const currentUserId = computed(() => {
  const id = Number(authStore.currentUser?.userId);
  return Number.isFinite(id) && id > 0 ? id : null;
});

const projectNameById = computed(() => {
  const map = new Map<number, string>();
  for (const p of props.projects) map.set(Number(p.id), p.name || `项目#${p.id}`);
  return map;
});

const distributionCounts = computed(() => {
  const acc: Record<OverviewUiStatus, number> = {
    待接受: 0,
    解决中: 0,
    已解决: 0,
    已延期: 0,
  };
  for (const row of issueRows.value) {
    acc[row.status] += 1;
  }
  return acc;
});

const distributionItems = computed(() =>
  OVERVIEW_STATUS_META.map((m) => ({
    ...m,
    count: distributionCounts.value[m.label] ?? 0,
  })),
);

const handlerFilterOptions = computed(() =>
  projectMemberOptions.value.map((m) => {
    const value = Number(m.userId);
    const label = String(m.realName ?? '').trim() || memberNameById.value.get(value) || `用户 #${value}`;
    return { value, label };
  }),
);

const filteredStatRows = computed(() => {
  const sev = filterSeverity.value;
  const st = filterStatus.value;
  const cQ = filterCreator.value.trim().toLowerCase();
  const handlerId = filterHandler.value === '' ? null : Number(filterHandler.value);
  const dQ = filterResolvedDate.value;
  return issueRows.value.filter((row) => {
    if (sev && row.priority !== sev) return false;
    if (st && row.status !== st) return false;
    if (cQ && !row.creator.toLowerCase().includes(cQ)) return false;
    // 多处理人：选中成员只要是处理人之一即保留
    if (handlerId != null && Number.isFinite(handlerId) && handlerId > 0) {
      if (!row.assigneeUserIds.some((id) => Number(id) === handlerId)) return false;
    }
    if (dQ && row.resolvedAt !== dQ) return false;
    return true;
  });
});

const mineRowsBase = computed(() => {
  const uid = currentUserId.value;
  if (uid == null) return [];
  return issueRows.value
    .filter((row) => row.assigneeUserIds.includes(uid) && row.status !== '已解决')
    .slice()
    .sort((a, b) => {
      const sr = severityRank(a.priority) - severityRank(b.priority);
      if (sr !== 0) return sr;
      const da = a.dueDate || a.resolvedAt || '9999-99-99';
      const db = b.dueDate || b.resolvedAt || '9999-99-99';
      return da.localeCompare(db);
    });
});

const filteredMineRows = computed(() => {
  const sev = mineSeverity.value;
  const dQ = mineResolvedDate.value;
  return mineRowsBase.value.filter((row) => {
    if (sev && row.priority !== sev) return false;
    if (dQ && (row.resolvedAt || row.dueDate) !== dQ) return false;
    return true;
  });
});

const selectProjectRows = computed((): OverviewSelectProjectRow[] =>
  props.projects.map((p) => ({
    id: Number(p.id),
    name: p.name || `项目#${p.id}`,
    phaseLabel: phaseLabelOf(p),
    issueCount: issueCountByProject.value.get(Number(p.id)) ?? 0,
  })),
);

watch(
  () => props.modelValue,
  (open) => {
    if (!open) {
      disposeChart();
      persistOverviewFilters();
    }
  },
);

watch(distributionItems, async () => {
  if (!props.modelValue) return;
  await nextTick();
  renderChart();
});

watch(
  [
    filterSeverity,
    filterStatus,
    filterCreator,
    filterHandler,
    filterResolvedDate,
    mineSeverity,
    mineResolvedDate,
  ],
  () => {
    if (!props.modelValue) return;
    persistOverviewFilters();
  },
);

onBeforeUnmount(() => disposeChart());

function phaseLabelOf(p: Project): string {
  const code = String(p.currentPhaseCode ?? '').trim().toUpperCase();
  if (code === 'PLAN') return PHASE_CODE_LABEL.PLAN;
  const name = String(p.currentPhaseName ?? '').trim();
  if (name) return name;
  return PHASE_CODE_LABEL[code] || '—';
}

function prefsUserId() {
  const id = Number(authStore.currentUser?.userId);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function resetFilters() {
  filterSeverity.value = '';
  filterStatus.value = '';
  filterCreator.value = '';
  filterHandler.value = '';
  filterResolvedDate.value = null;
  mineSeverity.value = '';
  mineResolvedDate.value = null;
}

function restoreOverviewFilters() {
  const saved = getIssueOverviewFilters(prefsUserId());
  filterSeverity.value = String(saved.severity ?? '');
  filterStatus.value = String(saved.status ?? '');
  filterCreator.value = String(saved.creator ?? '');
  const hid = saved.handlerUserId;
  filterHandler.value =
    hid !== '' && hid != null && Number(hid) > 0 ? Number(hid) : '';
  filterResolvedDate.value = saved.resolvedDate ?? null;
  mineSeverity.value = String(saved.mineSeverity ?? '');
  mineResolvedDate.value = saved.mineResolvedDate ?? null;
}

function persistOverviewFilters() {
  setIssueOverviewFilters(prefsUserId(), {
    severity: filterSeverity.value || undefined,
    status: filterStatus.value || undefined,
    creator: filterCreator.value.trim() || undefined,
    handlerUserId: filterHandler.value !== '' ? Number(filterHandler.value) : '',
    resolvedDate: filterResolvedDate.value || null,
    mineSeverity: mineSeverity.value || undefined,
    mineResolvedDate: mineResolvedDate.value || null,
  });
}

function collectSelectedProjectMemberIds(): number[] {
  const selected = new Set(selectedProjectIds.value.map(Number));
  const ids = new Set<number>();
  for (const p of props.projects) {
    if (!selected.has(Number(p.id))) continue;
    if (p.projectLeadUserId != null && Number(p.projectLeadUserId) > 0) {
      ids.add(Number(p.projectLeadUserId));
    }
    for (const mid of p.projectMemberUserIds || []) {
      const n = Number(mid);
      if (n > 0) ids.add(n);
    }
    if (p.creatorUserId != null && Number(p.creatorUserId) > 0) {
      ids.add(Number(p.creatorUserId));
    }
  }
  return [...ids];
}

async function resolveProjectMemberOptions() {
  const ids = collectSelectedProjectMemberIds();
  if (!ids.length) {
    projectMemberOptions.value = [];
    return;
  }
  try {
    const members: ProjectMemberOption[] = await listProjectMemberCandidates('', ids);
    const byId = new Map<number, ProjectMemberOption>();
    for (const m of members) {
      const id = Number(m.userId);
      if (id > 0) byId.set(id, m);
    }
    // 保证已选项目成员都在下拉里（无姓名时仍展示）
    projectMemberOptions.value = ids
      .map((id) => byId.get(id) || ({ userId: id, realName: memberNameById.value.get(id) || `用户 #${id}` }))
      .sort((a, b) => String(a.realName || '').localeCompare(String(b.realName || ''), 'zh-CN'));
    const map = new Map(memberNameById.value);
    for (const m of projectMemberOptions.value) {
      const id = Number(m.userId);
      const name = String(m.realName ?? '').trim();
      if (id > 0 && name) map.set(id, name);
    }
    memberNameById.value = map;
    if (
      filterHandler.value !== ''
      && !ids.includes(Number(filterHandler.value))
    ) {
      filterHandler.value = '';
    }
  } catch {
    projectMemberOptions.value = ids.map((id) => ({
      userId: id,
      realName: memberNameById.value.get(id) || `用户 #${id}`,
    }));
  }
}

function memberLabel(uid: number): string {
  return memberNameById.value.get(uid) || `用户 #${uid}`;
}

function resolveCreator(t: TaskItem): string {
  const fromApi = String(t.creatorDisplayName ?? '').trim();
  if (fromApi) return fromApi;
  if (t.yinoAiCreated) return '一诺';
  if (t.creatorUserId != null && Number(t.creatorUserId) > 0) {
    return memberLabel(Number(t.creatorUserId));
  }
  return '—';
}

function resolveHandler(t: TaskItem): string {
  const ids = parseAssigneeIds(t);
  if (ids.length) return ids.map((id) => memberLabel(id)).join('、');
  const assignee = String(t.assignee ?? '').trim();
  return assignee && !/^\d+$/.test(assignee) ? assignee : '—';
}

function mapTask(t: TaskItem, projectId: number, projectName: string): OverviewIssueRow {
  const status = mapTaskStatusToOverviewUi(t);
  return {
    id: Number(t.id),
    projectId,
    projectName,
    issueLabel: resolveIssueLabel(t),
    description: resolveIssueDescription(t),
    category: resolveIssueCategory(t),
    phase: t.phaseCode === 'PLAN' ? PHASE_CODE_LABEL.PLAN : String(t.phaseName ?? '').trim() || '—',
    severity: severityFromPriority(t.priority),
    status,
    creator: resolveCreator(t),
    handler: resolveHandler(t),
    resolvedAt: status === '已解决' ? formatDateOnly(t.closedAt) || formatDateOnly(t.updatedAt) : '',
    dueDate: formatDateOnly(t.dueDate),
    priority: normalizePriority(t.priority),
    assigneeUserIds: parseAssigneeIds(t),
  };
}

async function resolveMemberNames(tasks: TaskItem[]) {
  const ids = new Set<number>();
  for (const t of tasks) {
    for (const id of parseAssigneeIds(t)) ids.add(id);
    if (t.creatorUserId != null && Number(t.creatorUserId) > 0) ids.add(Number(t.creatorUserId));
  }
  if (!ids.size) {
    memberNameById.value = new Map();
    return;
  }
  try {
    const members: ProjectMemberOption[] = await listProjectMemberCandidates('', [...ids]);
    const map = new Map<number, string>();
    for (const m of members) {
      const id = Number(m.userId);
      const name = String(m.realName ?? '').trim();
      if (id > 0 && name) map.set(id, name);
    }
    memberNameById.value = map;
  } catch {
    memberNameById.value = new Map();
  }
}

async function loadIssuesForSelected() {
  const ids = selectedProjectIds.value;
  if (!ids.length) {
    issueRows.value = [];
    issueCountByProject.value = new Map();
    projectMemberOptions.value = [];
    await nextTick();
    renderChart();
    return;
  }
  loading.value = true;
  try {
    const pages = await mapPool(ids, 5, async (projectId) => {
      try {
        const page = await fetchAllIssueChecklistPages((pageNumber, pageSize) =>
          listProjectIssueChecklist(projectId, { page: pageNumber, size: pageSize }),
        );
        return { projectId, records: page.records, total: page.total };
      } catch (e) {
        console.error(e);
        return { projectId, records: [] as TaskItem[], total: 0 };
      }
    });
    const countMap = new Map<number, number>();
    const allTasks: TaskItem[] = [];
    const rows: OverviewIssueRow[] = [];
    for (const p of pages) {
      countMap.set(p.projectId, p.total);
      const name = projectNameById.value.get(p.projectId) || `项目#${p.projectId}`;
      for (const t of p.records) {
        allTasks.push(t);
        rows.push(mapTask(t, p.projectId, name));
      }
    }
    await resolveMemberNames(allTasks);
    await resolveProjectMemberOptions();
    // 名称解析后重算展示字段
    issueRows.value = allTasks.map((t, i) => {
      const base = rows[i];
      return {
        ...base,
        creator: resolveCreator(t),
        handler: resolveHandler(t),
      };
    });
    issueCountByProject.value = countMap;
  } catch (e: unknown) {
    console.error(e);
    ElMessage.error((e as Error)?.message || '加载问题统计失败');
    issueRows.value = [];
    projectMemberOptions.value = [];
  } finally {
    loading.value = false;
    await nextTick();
    renderChart();
  }
}

function disposeChart() {
  chart?.dispose();
  chart = null;
}

function renderChart() {
  const el = chartElRef.value;
  if (!el) return;
  if (!chart) chart = echarts.init(el);
  const items = distributionItems.value.filter((x) => x.count > 0);
  const data = items.length
    ? items.map((x) => ({ name: x.label, value: x.count, itemStyle: { color: x.color } }))
    : [{ name: '暂无数据', value: 1, itemStyle: { color: '#e4e7ed' } }];
  const empty = items.length === 0;
  chart.setOption(
    {
      animationDuration: 400,
      tooltip: empty
        ? { show: false }
        : {
            trigger: 'item',
            formatter: (p: { name: string; value: number }) => `${p.name}：${p.value}`,
          },
      series: [
        {
          type: 'pie',
          radius: ['58%', '86%'],
          center: ['50%', '72%'],
          startAngle: 180,
          endAngle: 360,
          clockwise: true,
          avoidLabelOverlap: false,
          label: { show: false },
          labelLine: { show: false },
          itemStyle: { borderColor: '#fff', borderWidth: 2 },
          data,
        },
      ],
    },
    true,
  );
}

async function onOpened() {
  restoreOverviewFilters();
  selectedProjectIds.value = props.projects.map((p) => Number(p.id)).filter((id) => id > 0);
  if (!selectedProjectIds.value.length) {
    issueRows.value = [];
    ElMessage.info('暂无参与的项目');
    await nextTick();
    renderChart();
    return;
  }
  await loadIssuesForSelected();
}

async function onSelectConfirm(ids: number[]) {
  selectedProjectIds.value = ids;
  await loadIssuesForSelected();
}

function onViewIssue(row: OverviewIssueRow) {
  emit('update:modelValue', false);
  void router.push({
    name: 'ProjectDashboard',
    params: { id: String(row.projectId) },
    query: { tab: 'issues' },
  });
}

function severityPillClass(sev: string) {
  if (sev === '致命') return 'pill--fatal';
  if (sev === '严重' || sev === '中风险') return 'pill--serious';
  return 'pill--normal';
}

function statusPillClass(st: string) {
  if (st === '已解决') return 'pill--resolved';
  if (st === '解决中') return 'pill--solving';
  if (st === '已延期') return 'pill--delayed';
  return 'pill--pending';
}
</script>

<style scoped lang="scss">
.ov-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-right: 28px;
}

.ov-title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.ov-select-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: #fff;
  color: #606266;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    border-color: #c0c4cc;
    color: #303133;
  }
}

.ov-select-chevron {
  font-size: 12px;
  color: #909399;
}

.ov-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-height: 520px;
}

.ov-top {
  display: grid;
  grid-template-columns: minmax(280px, 340px) minmax(0, 1fr);
  gap: 14px;
  align-items: stretch;
}

.ov-card {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 14px 16px;
  background: #fff;
  min-width: 0;
}

.ov-card-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.ov-card-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px 14px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.ov-chart {
  width: 100%;
  height: 140px;
  margin-top: 4px;
}

.ov-metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-top: 8px;
}

.ov-metric {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 8px 10px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  min-width: 0;
}

.ov-metric-label {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
}

.ov-metric-value {
  font-size: 18px;
  font-weight: 600;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
}

.ov-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px 12px;
}

.ov-filter {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ov-filter-label {
  font-size: 12px;
  color: #606266;
  white-space: nowrap;
}

.ov-filter-select {
  width: 100px;
}

.ov-filter-search {
  width: 118px;
}

.ov-filter-date {
  width: 140px;
}

.ov-mine-title-wrap {
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
}

.ov-mine-hint {
  font-size: 12px;
  color: #909399;
}

.ov-table {
  width: 100%;

  :deep(.el-table__header th) {
    background: transparent;
    color: #909399;
    font-weight: 500;
    font-size: 13px;
  }

  :deep(.el-table__row) {
    font-size: 13px;
    color: #303133;
  }

  :deep(.el-table__body tr.current-row > td.el-table__cell) {
    background: #f0f7ff;
  }

  :deep(.el-table__inner-wrapper::before) {
    display: none;
  }
}

.ov-table--stripe {
  :deep(.el-table__body tr:nth-child(even) > td.el-table__cell) {
    background: #fafbfc;
  }
}

.pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 1.45;
  white-space: nowrap;
}

.pill--normal {
  color: #409eff;
  background: #ecf5ff;
}

.pill--fatal {
  color: #f56c6c;
  background: #fef0f0;
}

.pill--serious {
  color: #e6a23c;
  background: #fdf6ec;
}

.pill--resolved {
  color: #67c23a;
  background: #f0f9eb;
}

.pill--pending {
  color: #409eff;
  background: #ecf5ff;
}

.pill--solving {
  color: #e6a23c;
  background: #fdf6ec;
}

.pill--delayed {
  color: #f56c6c;
  background: #fef0f0;
}

.cat-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  padding: 2px 8px;
  border-radius: 4px;
  color: #fff;
  font-size: 12px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (max-width: 960px) {
  .ov-top {
    grid-template-columns: 1fr;
  }

  .ov-metrics {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

@media (max-width: 640px) {
  .ov-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>

<style lang="scss">
.project-overview-dialog.el-dialog {
  border-radius: 10px;
  overflow: hidden;
}

.project-overview-dialog .el-dialog__header {
  padding: 16px 20px 10px;
  margin-right: 0;
}

.project-overview-dialog .el-dialog__headerbtn {
  top: 16px;
  right: 16px;
}

.project-overview-dialog .el-dialog__body {
  padding: 4px 20px 18px;
}
</style>
